<template>
    <div>
        <div class="px-4 pt-10 pb-10 mx-auto lg:pt-16 lg:pb-20 lg:px-8 xl:px-12" :style="{ backgroundColor: backgroundColor }">
           <div class="mx-auto max-w-6xl">
            <h3 class="lg:mb-12 mb-6 leading-none text-center text-3xl md:text-4xl font-ww-bold xl:!leading-none xl:text-42 xl:px-12" v-html="heading">
            </h3>
            <div class="flex gap-x-5 mt-10 lg:mt-4 lg:gap-x-12 md-max:flex-wrap md-max:gap-y-5">
                <!-- Left Column -->
                <div class="space-y-5 md:flex-1 lg:space-y-10 md-max:w-full">
                    <div v-for="(testimonial, index) in leftColumnTestimonials" :key="'left-' + index" class="px-6 py-4 bg-white shadow-sm md:px-12 md:py-8 h-fit">
                        <h4 class="md:mb-6 mb-3 text-lg font-ww-bold md:text-xl xl:text-2xl md-max:!leading-6" v-html="testimonial.title">
                        </h4>
                        <div class="xl:text-lg sm:text-base text-sm mb-4 !leading-5 font-ww-regular" v-html="testimonial.text">
                        </div>
                        <div class="flex items-center mt-10 mb-2">
                            <nuxt-img :src="starsImageSrc" width="96" height="15" alt="5 stars" loading="lazy" />
                        </div>
                        <div class="text-base xl:text-xl sm:text-lg font-ww-medium">
                            {{ testimonial.name }}
                        </div>
                        <div class="text-sm xl:text-lg sm:text-base font-ww-regular">
                            {{ testimonial.location }}
                        </div>
                    </div>
                </div>
                
                <!-- Right Column -->
                <div class="space-y-5 md:flex-1 lg:space-y-10 md-max:w-full">
                    <div v-for="(testimonial, index) in rightColumnTestimonials" :key="'right-' + index" class="px-6 py-4 bg-white shadow-sm md:px-12 md:py-8 h-fit">
                        <h4 class="md:mb-6 mb-3 text-lg font-ww-bold md:text-xl xl:text-2xl md-max:!leading-6" v-html="testimonial.title">
                        </h4>
                        <div class="xl:text-lg sm:text-base text-sm mb-4 !leading-5 font-ww-regular" v-html="testimonial.text">
                        </div>
                        <div class="flex items-center mt-10 mb-2">
                            <nuxt-img :src="starsImageSrc" width="96" height="15" alt="5 stars" loading="lazy" />
                        </div>
                        <div class="text-base xl:text-xl sm:text-lg font-ww-medium">
                            {{ testimonial.name }}
                        </div>
                        <div class="text-sm xl:text-lg sm:text-base font-ww-regular">
                            {{ testimonial.location }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="mt-12 text-center">
                <button @click="scrollTo(buttonHref,'bottom')"
                    class="inline-flex justify-center items-center px-8 py-4 text-lg font-bold text-white rounded-full transition-colors duration-200 lg:w-6/12 font-ww-medium btn-red hover:bg-red-600">
                    {{ buttonText }}
                    <svg class="ml-2 w-5 h-5" width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.3007 0.50283C13.2069 0.595793 13.1325 0.706394 13.0818 0.828253C13.031 0.950112 13.0049 1.08082 13.0049 1.21283C13.0049 1.34484 13.031 1.47555 13.0818 1.59741C13.1325 1.71927 13.2069 1.82987 13.3007 1.92283L17.9007 6.52283L1.5 6.33529C1.23478 6.33529 0.98043 6.44065 0.792893 6.62819C0.605357 6.81572 0.5 7.07008 0.5 7.33529C0.5 7.60051 0.605357 7.85486 0.792893 8.0424C0.98043 8.22994 1.23478 8.33529 1.5 8.33529L17.8807 8.52283L13.3007 13.0928C13.1144 13.2802 13.0099 13.5336 13.0099 13.7978C13.0099 14.062 13.1144 14.3155 13.3007 14.5028C13.488 14.6891 13.7415 14.7936 14.0057 14.7936C14.2699 14.7936 14.5233 14.6891 14.7107 14.5028L21.0707 8.14283C21.1563 8.0607 21.2244 7.96211 21.2709 7.85299C21.3174 7.74386 21.3414 7.62646 21.3414 7.50783C21.3414 7.3892 21.3174 7.2718 21.2709 7.16267C21.2244 7.05355 21.1563 6.95496 21.0707 6.87283L14.7207 0.50283C14.6277 0.409101 14.5171 0.334707 14.3952 0.283938C14.2734 0.23317 14.1427 0.207031 14.0107 0.207031C13.8787 0.207031 13.748 0.23317 13.6261 0.283938C13.5042 0.334707 13.3936 0.409101 13.3007 0.50283Z" fill="white"/>
                    </svg>
                </button>
            </div>
            </div>
        </div>
    </div>
</template>

<script setup>
// Split testimonials into two columns
const leftColumnTestimonials = computed(() => {
  return props.testimonials.filter((_, index) => index % 2 === 0);
});

const rightColumnTestimonials = computed(() => {
  return props.testimonials.filter((_, index) => index % 2 === 1);
});

const props = defineProps({
    heading: {
        type: String,
        default: 'Real Stories from Real Minds'
    },
    backgroundColor: {
        type: String,
        default: '#f5f5f5'
    },
    starsImageSrc: {
        type: String,
        default: '/assets/images/svg-rating.svg'
    },
    buttonText: {
        type: String,
        default: 'ORDER NOW'
    },
    buttonHref: {
        type: String,
        default: 'products'
    },
    testimonials: {
        type: Array,
        default: () => [
            {
                title: 'Keeps Me Focused and Energized',
                text: '"As a full-time student and part-time content creator, staying focused without crashing is non-negotiable. Mindflow Coffee has been a game-changer for me. It gives me that clean, steady energy boost without the jitters or burnout I used to get from regular coffee. Plus, knowing it\'s designed specifically for Gen Z makes me feel like it actually gets me. Brain fog? Gone. Procrastination? Who\'s she? Mindflow keeps me sharp, creative, and in the zone all day."',
                name: 'Ravi S.',
                location: 'India'
            },
            {
                title: 'Mindflow Coffee is a total game-changer',
                text: 'I used to hit that 3 PM slump hard, but now I feel focused and sharp all day—without the jitters or crash. It\'s like brain fuel in a cup, perfect for studying or grinding through work.',
                name: 'Ava L.',
                location: 'United States'
            },
            {
                title: 'Mindflow helps me hit that productive zone without the anxiety I used to get from regular coffee',
                text: 'I\'ve never had coffee that keeps me this focused for so long. Mindflow feels like mental clarity in a mug.',
                name: 'Sophie Long',
                location: 'Seattle'
            },
            {
                title: 'With Mindflow, I get into deep work so much faster',
                text: 'I use Mindflow before client calls and creative sessions. It gives me calm, steady energy without that jittery feeling. Total game-changer.',
                name: 'Jordan Smith',
                location: 'Chicago'
            },
            {
                title: 'It\'s a Creative Lifesaver',
                text: 'Mindflow Coffee gives me that mental clarity I need for editing videos and creating content. No brain fog, just flow. It\'s honestly wild how smooth it feels—like, I\'ll be editing for hours without even realizing time passed. I used to rely on sugary drinks or regular coffee, but they always left me feeling drained after a while. With Mindflow, I\'m energized and still feel good after.',
                name: 'Anonymous',
                location: 'Australia'
            },
            {
                title: 'Highly recommend if you\'re balancing a busy schedule like I am.',
                text: 'I\'ve tried tons of energy drinks and coffees, but this one\'s different. It keeps me focused without making me feel wired or anxious. I can work on my startup tasks, hop on Zoom meetings, and still have mental energy left over in the evening. Mindflow really gets what our generation needs—it\'s not just caffeine, it\'s designed to support focus and productivity without the crash.',
                name: 'Lucas M.',
                location: 'Canada'
            }
        ]
    }
})

defineBuilderComponent({
    friendlyName: 'TestimonialsListInfoLeft',
    canHaveChildren: false,
    inputs: [
        {
            name: 'heading',
            type: 'richText',
            defaultValue: 'Real Stories from Real Minds'
        },
        {
            name: 'backgroundColor',
            type: 'color',
            defaultValue: '#f5f5f5'
        },
        {
            name: 'starsImageSrc',
            type: 'file',
            defaultValue: '/assets/images/svg-rating.svg'
        },
        {
            name: 'buttonText',
            type: 'text',
            defaultValue: 'ORDER NOW'
        },
        {
            name: 'buttonHref',
            type: 'text',
            defaultValue: 'products'
        },
        {
            name: 'testimonials',
            type: 'list',
            subFields: [
                {
                    name: 'title',
                    type: 'richText'
                },
                {
                    name: 'text',
                    type: 'richText'
                },
                {
                    name: 'name',
                    type: 'text'
                },
                {
                    name: 'location',
                    type: 'text'
                }
            ],
            defaultValue: [
                {
                    title: 'Keeps Me Focused and Energized',
                    text: '"As a full-time student and part-time content creator, staying focused without crashing is non-negotiable. Mindflow Coffee has been a game-changer for me. It gives me that clean, steady energy boost without the jitters or burnout I used to get from regular coffee. Plus, knowing it\'s designed specifically for Gen Z makes me feel like it actually gets me. Brain fog? Gone. Procrastination? Who\'s she? Mindflow keeps me sharp, creative, and in the zone all day."',
                    name: 'Ravi S.',
                    location: 'India'
                },
                {
                    title: 'Mindflow Coffee is a total game-changer',
                    text: 'I used to hit that 3 PM slump hard, but now I feel focused and sharp all day—without the jitters or crash. It\'s like brain fuel in a cup, perfect for studying or grinding through work.',
                    name: 'Ava L.',
                    location: 'United States'
                },
                {
                    title: 'Mindflow helps me hit that productive zone without the anxiety I used to get from regular coffee',
                    text: 'I\'ve never had coffee that keeps me this focused for so long. Mindflow feels like mental clarity in a mug.',
                    name: 'Sophie Long',
                    location: 'Seattle'
                },
                {
                    title: 'With Mindflow, I get into deep work so much faster',
                    text: 'I use Mindflow before client calls and creative sessions. It gives me calm, steady energy without that jittery feeling. Total game-changer.',
                    name: 'Jordan Smith',
                    location: 'Chicago'
                },
                {
                    title: 'It\'s a Creative Lifesaver',
                    text: 'Mindflow Coffee gives me that mental clarity I need for editing videos and creating content. No brain fog, just flow. It\'s honestly wild how smooth it feels—like, I\'ll be editing for hours without even realizing time passed. I used to rely on sugary drinks or regular coffee, but they always left me feeling drained after a while. With Mindflow, I\'m energized and still feel good after.',
                    name: 'Anonymous',
                    location: 'Australia'
                },
                {
                    title: 'Highly recommend if you\'re balancing a busy schedule like I am.',
                    text: 'I\'ve tried tons of energy drinks and coffees, but this one\'s different. It keeps me focused without making me feel wired or anxious. I can work on my startup tasks, hop on Zoom meetings, and still have mental energy left over in the evening. Mindflow really gets what our generation needs—it\'s not just caffeine, it\'s designed to support focus and productivity without the crash.',
                    name: 'Lucas M.',
                    location: 'Canada'
                }
            ]
        }
    ]
})
</script>